/**
 * Model registry — maps runtime adapter types to available models.
 *
 * Dynamic: reads from companion-reported models when available,
 * falls back to a static list when the companion hasn't connected.
 */

import { useCompanionStore } from "@/stores/companion-store";
import type {
  AvailableModel,
  AvailableModelsMap,
} from "@/stores/companion-store";
import {
  isRuntimeKey,
  getRuntimeDefaultModel as getDefaultModel,
  RUNTIME_DEFAULT_MODEL,
  type RuntimeAvailability,
  type RuntimeKey,
} from "@/lib/runtime-defaults";
import { findCompanionModel } from "@/lib/companion-model-lookup";
import { getModelEffortConfig } from "@/lib/model-effort";
import { compareModelVersions } from "@/lib/model-picker-options";
import { translate } from "@/i18n";
import { libLabelsMessages } from "@/i18n/messages/lib-labels";

export {
  getModelEffortConfig,
  MODEL_EFFORT,
  modelSupportsThinkingEffort,
} from "@/lib/model-effort";
export type {
  EffortChoice,
  EffortValue,
  ModelEffortConfig,
} from "@/lib/model-effort";
export {
  getPreferredRuntimeSource,
  isRuntimeKey,
  MANAGED_OPENCODE_DEFAULT_MODEL,
  resolveTemplateModel,
  RUNTIME_DEFAULT_MODEL,
} from "@/lib/runtime-defaults";
export type { RuntimeKey } from "@/lib/runtime-defaults";

export type RuntimeModelKey = RuntimeKey | (string & {});

export interface ModelOption {
  value: string;
  label: string;
  group?: string;
  free?: boolean;
  /**
   * Provider id behind the group. Only set for plan-grouped OpenCode
   * options, where the group is a plan label the provider id can't be
   * derived back from ("opencode-go" → "Go").
   */
  provider?: string;
}

/**
 * Personal OpenCode resells the same model families through several plans,
 * so its picker groups by the plan a model bills against — the provider id
 * the companion reports per model — instead of by vendor.
 *
 * One ordered table, keyed by provider id: rank is the index, so a personal
 * provider key can never inherit a plan's rank by sharing its display label.
 * Flat-rate comes first (already paid), then metered, then credits.
 */
const OPENCODE_PLANS: readonly {
  provider: string;
  label: string;
  hint: "planSubscription" | "planPayPerToken" | "planCredits";
}[] = [
  { provider: "opencode-go", label: "Go", hint: "planSubscription" },
  { provider: "opencode", label: "Zen", hint: "planPayPerToken" },
  { provider: "openrouter", label: "OpenRouter", hint: "planCredits" },
];

function opencodePlanLabel(provider: string): string {
  return (
    OPENCODE_PLANS.find((plan) => plan.provider === provider)?.label ??
    provider.charAt(0).toUpperCase() + provider.slice(1)
  );
}

/** Personal provider keys rank after every known plan. */
function opencodePlanRank(provider: string | undefined): number {
  const index = OPENCODE_PLANS.findIndex((plan) => plan.provider === provider);
  return index === -1 ? OPENCODE_PLANS.length : index;
}

/** How a plan bills — the muted hint beside its header. */
export function opencodePlanBillingHint(
  provider: string | undefined,
): string | undefined {
  const hint = OPENCODE_PLANS.find((plan) => plan.provider === provider)?.hint;
  return hint && translate(libLabelsMessages, hint);
}

/**
 * Plan grouping covers only the companion-discovered personal catalog.
 *
 * Two things must hold. The runtime key must be the bare one — managed keys
 * ("opencode_cli:denker_starter") carry a single plan, Denker's own. And a
 * personal OpenCode must actually be installed: with only the bundled
 * runtime the companion mirrors Denker's managed catalog onto the bare key,
 * where an "OpenRouter · credits" header would be a fiction.
 */
export function usesOpencodePlanGroups(
  runtime: RuntimeKey,
  availableRuntimes: readonly RuntimeAvailability[],
  runtimeModelKey?: RuntimeModelKey,
): boolean {
  if (runtime !== "opencode_cli") return false;
  if (runtimeModelKey && runtimeModelKey !== runtime) return false;
  return availableRuntimes.some(
    (item) => item.type === "opencode_cli" && !item.managed,
  );
}

const MODEL_ENRICHMENT: Record<string, { label: string; group: string }> = {
  // Legacy short names, labelled with what Claude Code 2.1.291 resolves them to.
  default: { label: "Claude Opus 5.5", group: "Anthropic" },
  best: { label: "Claude Opus 5.5", group: "Anthropic" },
  opus: { label: "Claude Opus 5.5", group: "Anthropic" },
  "opus[1m]": { label: "Claude Opus 5.5", group: "Anthropic" },
  opusplan: { label: "Claude Opus 5.5 Plan", group: "Anthropic" },
  sonnet: { label: "Claude Sonnet 5.5", group: "Anthropic" },
  "sonnet[1m]": { label: "Claude Sonnet 5.5", group: "Anthropic" },
  haiku: { label: "Claude Haiku 4.5", group: "Anthropic" },
  "claude-fable-5-1": { label: "Claude Fable 5.1", group: "Anthropic" },
  "claude-fable-5": { label: "Claude Fable 5", group: "Anthropic" },
  "claude-opus-4-8": { label: "Claude Opus 4.8", group: "Anthropic" },
  "claude-opus-4-8[1m]": { label: "Claude Opus 4.8 (1M)", group: "Anthropic" },
  "claude-opus-4-7": { label: "Claude Opus 4.7", group: "Anthropic" },
  "claude-opus-4-7[1m]": { label: "Claude Opus 4.7 (1M)", group: "Anthropic" },
  "claude-opus-4-5": { label: "Claude Opus 4.5", group: "Anthropic" },
  "claude-opus-4-1": { label: "Claude Opus 4.1", group: "Anthropic" },
  "claude-sonnet-4-6": { label: "Claude Sonnet 4.6", group: "Anthropic" },
  "claude-sonnet-4-6[1m]": {
    label: "Claude Sonnet 4.6 (1M)",
    group: "Anthropic",
  },
  "claude-opus-5-5": { label: "Claude Opus 5.5", group: "Anthropic" },
  "claude-sonnet-5-5": { label: "Claude Sonnet 5.5", group: "Anthropic" },
  "claude-sonnet-5": { label: "Claude Sonnet 5", group: "Anthropic" },
  "claude-sonnet-4-5": { label: "Claude Sonnet 4.5", group: "Anthropic" },
  "claude-sonnet-4": { label: "Claude Sonnet 4", group: "Anthropic" },
  "claude-3-5-haiku": { label: "Claude 3.5 Haiku", group: "Anthropic" },
  "claude-haiku-4-5": { label: "Claude Haiku 4.5", group: "Anthropic" },
  "claude-haiku-4-5-20251001": {
    label: "Claude Haiku 4.5",
    group: "Anthropic",
  },
  "gemini-3.1-pro": { label: "Gemini 3.1 Pro", group: "Google" },
  "gemini-3-pro": { label: "Gemini 3 Pro", group: "Google" },
  "gemini-3-flash": { label: "Gemini 3 Flash", group: "Google" },
  "gpt-5.5": { label: "GPT-5.5", group: "OpenAI" },
  "gpt-6.1-sol": { label: "GPT-6.1 Sol", group: "OpenAI" },
  "gpt-6-sol": { label: "GPT-6 Sol", group: "OpenAI" },
  "gpt-6-astra": { label: "GPT-6 Astra", group: "OpenAI" },
  "gpt-6-luna": { label: "GPT-6 Luna", group: "OpenAI" },
  "gpt-5.6-sol": { label: "GPT-5.6 Sol", group: "OpenAI" },
  "gpt-5.6-terra": { label: "GPT-5.6 Terra", group: "OpenAI" },
  "gpt-5.6-luna": { label: "GPT-5.6 Luna", group: "OpenAI" },
  "gpt-5.4": { label: "GPT-5.4", group: "OpenAI" },
  "gpt-5.4-pro": { label: "GPT-5.4 Pro", group: "OpenAI" },
  "gpt-5.4-mini": { label: "GPT-5.4 Mini", group: "OpenAI" },
  "gpt-5.4-nano": { label: "GPT-5.4 Nano", group: "OpenAI" },
  "gpt-5.3-codex-spark": { label: "GPT-5.3 Codex Spark", group: "OpenAI" },
  "gpt-5.3-codex": { label: "GPT-5.3 Codex", group: "OpenAI" },
  "gpt-5.2": { label: "GPT-5.2", group: "OpenAI" },
  "gpt-5.2-codex": { label: "GPT-5.2 Codex", group: "OpenAI" },
  "gpt-5.1": { label: "GPT-5.1", group: "OpenAI" },
  "gpt-5.1-codex-max": { label: "GPT-5.1 Codex Max", group: "OpenAI" },
  "gpt-5.1-codex": { label: "GPT-5.1 Codex", group: "OpenAI" },
  "gpt-5.1-codex-mini": { label: "GPT-5.1 Codex Mini", group: "OpenAI" },
  "gpt-5": { label: "GPT-5", group: "OpenAI" },
  "gpt-5-codex": { label: "GPT-5 Codex", group: "OpenAI" },
  "gpt-5-nano": { label: "GPT-5 Nano", group: "OpenAI" },
  "minimax-m2.5": { label: "Minimax M2.5", group: "Minimax" },
  "minimax-m2.5-free": { label: "Minimax M2.5", group: "Free" },
  "minimax-m2.1": { label: "Minimax M2.1", group: "Minimax" },
  "minimax-m3": { label: "MiniMax M3", group: "Minimax" },
  "openrouter/minimax/minimax-m3": { label: "MiniMax M3", group: "Minimax" },
  "openrouter/qwen/qwen3.6-plus": { label: "Qwen 3.6 Plus", group: "Qwen" },
  "openrouter/qwen/qwen3.8-flash": { label: "Qwen 3.8 Flash", group: "Qwen" },
  "qwen3.6-plus-free": { label: "Qwen 3.6 Plus", group: "Free" },
  "glm-5.1": { label: "GLM 5.1", group: "Zhipu" },
  "glm-5": { label: "GLM 5", group: "Zhipu" },
  "glm-4.7": { label: "GLM 4.7", group: "Zhipu" },
  "glm-4.6": { label: "GLM 4.6", group: "Zhipu" },
  "kimi-k2.5": { label: "Kimi K2.5", group: "Moonshot" },
  "kimi-k2": { label: "Kimi K2", group: "Moonshot" },
  "kimi-k2-thinking": { label: "Kimi K2 Thinking", group: "Moonshot" },
  "nemotron-3-super-free": { label: "Nemotron 3 Super", group: "Free" },
  "trinity-large-preview-free": { label: "Trinity Large", group: "Free" },
};

const FALLBACK_MODELS: Record<RuntimeKey, ModelOption[]> = {
  claude_cli: [
    { value: "claude-opus-5-5", label: "Claude Opus 5.5", group: "Anthropic" },
    {
      value: "claude-sonnet-5-5",
      label: "Claude Sonnet 5.5",
      group: "Anthropic",
    },
    {
      value: "claude-fable-5-1",
      label: "Claude Fable 5.1",
      group: "Anthropic",
    },
    { value: "claude-fable-5", label: "Claude Fable 5", group: "Anthropic" },
    { value: "claude-opus-4-8", label: "Claude Opus 4.8", group: "Anthropic" },
    {
      value: "claude-opus-4-8[1m]",
      label: "Claude Opus 4.8 (1M)",
      group: "Anthropic",
    },
    { value: "claude-opus-4-7", label: "Claude Opus 4.7", group: "Anthropic" },
    {
      value: "claude-opus-4-7[1m]",
      label: "Claude Opus 4.7 (1M)",
      group: "Anthropic",
    },
    {
      value: "claude-sonnet-5",
      label: "Claude Sonnet 5",
      group: "Anthropic",
    },
    {
      value: "claude-sonnet-4-6",
      label: "Claude Sonnet 4.6",
      group: "Anthropic",
    },
    {
      value: "claude-sonnet-4-6[1m]",
      label: "Claude Sonnet 4.6 (1M)",
      group: "Anthropic",
    },
    {
      value: "claude-haiku-4-5",
      label: "Claude Haiku 4.5",
      group: "Anthropic",
    },
  ],
  codex_cli: [
    { value: "gpt-6.1-sol", label: "GPT-6.1 Sol", group: "OpenAI" },
    { value: "gpt-6-sol", label: "GPT-6 Sol", group: "OpenAI" },
    { value: "gpt-6-astra", label: "GPT-6 Astra", group: "OpenAI" },
    { value: "gpt-6-luna", label: "GPT-6 Luna", group: "OpenAI" },
    { value: "gpt-5.6-sol", label: "GPT-5.6 Sol", group: "OpenAI" },
    { value: "gpt-5.6-terra", label: "GPT-5.6 Terra", group: "OpenAI" },
    { value: "gpt-5.6-luna", label: "GPT-5.6 Luna", group: "OpenAI" },
    { value: "gpt-5.5", label: "GPT-5.5", group: "OpenAI" },
  ],
  opencode_cli: [
    {
      value: "openrouter/qwen/qwen3.8-flash",
      label: "Qwen 3.8 Flash",
      group: "Qwen",
    },
    // Verified in `opencode models` (1.18.32) on 2026-10-07.
    {
      value: "opencode/qwen3.6-plus",
      label: "Qwen3.6 Plus",
      group: "OpenCode Zen",
    },
    {
      value: "opencode/big-pickle",
      label: "Big Pickle",
      group: "Free",
      free: true,
    },
  ],
};

/** Select personal defaults from its catalog without injecting another gateway. */
export function getRuntimeDefaultModel(
  runtime: RuntimeKey,
  runtimeSource?: string | null,
): string {
  const models = useCompanionStore.getState().availableModels[runtime];
  return getDefaultModel(runtime, runtimeSource, models);
}

export function getRuntimeDefaultEffort(runtime: RuntimeKey) {
  return getModelEffortConfig(RUNTIME_DEFAULT_MODEL[runtime]).default;
}

const ANTHROPIC_MODEL_ID =
  /^claude-(?<family>[a-z]+)-(?<major>\d+)(?:-(?<minor>\d{1,2}))?(?:-\d{8})?(?<extended>\[1m\])?$/;
/** Version-first ids Anthropic used before 4.x ("claude-3-5-haiku-20241022"). */
const LEGACY_ANTHROPIC_MODEL_ID =
  /^claude-(?<major>\d+)(?:-(?<minor>\d{1,2}))?-(?<family>[a-z]+)(?:-\d{8})?$/;

/** "claude-opus-5-5" → "Opus 5.5". Null when the id is not a versioned Claude id. */
export function anthropicModelName(modelId: string): string | null {
  const id = modelId.replace(/^opencode\//, "");
  const match =
    ANTHROPIC_MODEL_ID.exec(id) ?? LEGACY_ANTHROPIC_MODEL_ID.exec(id);
  const { family, major, minor, extended } = match?.groups ?? {};
  if (!family || !major) return null;
  const version = minor ? `${major}.${minor}` : major;
  const name = `${family.charAt(0).toUpperCase()}${family.slice(1)} ${version}`;
  return extended ? `${name} (1M)` : name;
}

function autoLabel(modelId: string): string {
  const anthropic = anthropicModelName(modelId);
  if (anthropic) return `Claude ${anthropic}`;
  const base = modelId.replace(/-free$/, "");
  return base
    .replace(/[-_.]/g, " ")
    .replace(/\b([a-z])/g, (c) => c.toUpperCase());
}

function autoGroup(provider: string): string {
  const map: Record<string, string> = {
    anthropic: "Anthropic",
    openai: "OpenAI",
    google: "Google",
    minimax: "Minimax",
    alibaba: "Alibaba",
    moonshot: "Moonshot",
    zhipu: "Zhipu",
    nvidia: "Nvidia",
    trinity: "Trinity",
    opencode: "Other",
  };
  return map[provider] ?? provider.charAt(0).toUpperCase() + provider.slice(1);
}

/**
 * Label for a companion-reported model; never a bare family name.
 *
 * The reported name wins when it already carries a version ("Opus 5.5",
 * "GPT-5.5"). A bare family ("Opus", "Default", "Opus (1M context)") or the
 * raw id the companion falls back to takes the version from the id instead.
 */
function companionModelLabel(model: AvailableModel): string {
  const reported = model.display_name?.trim() ?? "";
  const isRawId = reported === model.id || reported.startsWith("claude-");
  if (!isRawId && /\d/.test(reported.replace(/[([].*$/, ""))) return reported;
  if (reported) return anthropicModelName(model.id) ?? reported;
  return (
    MODEL_ENRICHMENT[model.id.replace(/^opencode\//, "")]?.label ??
    autoLabel(model.id)
  );
}

/**
 * Convert AvailableModel entries to ModelOption entries with enrichment.
 *
 * `planGrouped` swaps the group for the OpenCode plan the model bills
 * against. Enrichment groups are ignored there — an OpenRouter-hosted Qwen
 * belongs under "OpenRouter", not "Qwen" — while enrichment labels still
 * apply, and `free` stays a per-row flag instead of becoming a group.
 */
function toModelOptions(
  models: AvailableModel[],
  planGrouped = false,
): ModelOption[] {
  return models.map((m) => {
    const enriched = MODEL_ENRICHMENT[m.id.replace(/^opencode\//, "")];
    // The companion's name outranks the hardcoded enrichment label.
    const label = companionModelLabel(m);
    if (planGrouped) {
      return {
        value: m.id,
        label,
        group: opencodePlanLabel(m.provider),
        free: m.free,
        provider: m.provider,
      };
    }
    return {
      value: m.id,
      label,
      group: m.free ? "Free" : (enriched?.group ?? autoGroup(m.provider)),
      free: m.free,
    };
  });
}

/**
 * Sort model options: Free first, then group, then newest numeric version.
 */
function sortModelOptions(options: ModelOption[]): ModelOption[] {
  return [...options].sort((a, b) => {
    if (a.group === "Free" && b.group !== "Free") return -1;
    if (a.group !== "Free" && b.group === "Free") return 1;
    const groupCmp = (a.group ?? "").localeCompare(b.group ?? "");
    if (groupCmp !== 0) return groupCmp;
    return compareModelVersions(a, b);
  });
}

/**
 * Sort plan-grouped options: plan rank first (Go, Zen, OpenRouter, then
 * personal providers alphabetically), then newest numeric version.
 */
function sortPlanGroupedOptions(options: ModelOption[]): ModelOption[] {
  return [...options].sort((a, b) => {
    const rankCmp = opencodePlanRank(a.provider) - opencodePlanRank(b.provider);
    if (rankCmp !== 0) return rankCmp;
    const groupCmp = (a.group ?? "").localeCompare(b.group ?? "");
    if (groupCmp !== 0) return groupCmp;
    return compareModelVersions(a, b);
  });
}

/**
 * Get model options for a runtime. Uses dynamic models from companion
 * if available, falls back to static list.
 *
 * `planGrouped` comes from `usesOpencodePlanGroups` at the call site, so the
 * catalog and the grouping decision can never disagree — the caller passes
 * the same boolean on to the option mappers.
 */
export function getModelsForRuntime(
  runtime: RuntimeKey,
  availableModels?: AvailableModelsMap,
  runtimeModelKey?: RuntimeModelKey,
  planGrouped = false,
): ModelOption[] {
  const modelsByKey =
    availableModels ?? useCompanionStore.getState().availableModels;
  const dynamicModels =
    (runtimeModelKey ? modelsByKey[runtimeModelKey] : undefined) ??
    modelsByKey[runtime];
  if (dynamicModels && dynamicModels.length > 0) {
    if (planGrouped) {
      return sortPlanGroupedOptions(toModelOptions(dynamicModels, true));
    }
    return sortModelOptions(toModelOptions(dynamicModels));
  }
  return sortModelOptions(FALLBACK_MODELS[runtime] ?? []);
}

/**
 * Resolve a model id to its display label.
 */
export function formatModelLabel(
  adapterType: string | undefined,
  modelValue: string | undefined,
): string {
  if (!modelValue) return "\u2014";

  // 1) Provider-reported metadata wins. If the companion is connected
  //    and has told us about this model (or about an alias resolving
  //    to it), use the name it reports — that's the only way we pick up
  //    a fresh "Claude Opus 4.8" without a frontend ship.
  const fromRuntime =
    adapterType && isRuntimeKey(adapterType)
      ? useCompanionStore
          .getState()
          .availableModels[
            adapterType
          ]?.find((m) => m.id === modelValue || (m.aliases ?? []).includes(modelValue))
      : null;
  const companionMatch = fromRuntime ?? findCompanionModel(modelValue);
  if (companionMatch) return companionModelLabel(companionMatch);

  const enriched = MODEL_ENRICHMENT[modelValue.replace(/^opencode\//, "")];
  if (enriched) return enriched.label;

  return autoLabel(modelValue);
}
